/**
 * Monsterplan importeren: (deel)locaties en boorpunten uit een bestand op de vooronderzoekkaart.
 *
 * Leest wat de export schrijft (zie monsterplan-export.js), en ook wat een adviseur of
 * opdrachtgever aanlevert: GeoJSON, KML/KMZ, shapefile (.zip), DXF en een CSV/Excel-meetstaat.
 * De lezers zelf zijn die van de kaartimport (kaart-import/*); hier gebeurt alleen het uitsplitsen:
 *
 *   - vlakken (polygonen, gesloten polylijnen) → nieuwe deellocaties, met naam en strategie als
 *     het bestand die meegeeft
 *   - punten → geplande boorpunten; punten die volgens de export al geboord zijn slaan we over
 *
 * Het stelsel raden we aan de coördinaten (RD, UTM of WGS84), net als de kaartimport.
 */
import { NEN5740_STRATEGIES, guessCoordinateSystem, pointToWgs84, normalizeKind, parseDepthCm } from '@mv/models';
import { parseGeoJSON, parseKml, parseKmz, parseShapefileZip, guessSystemForFc, reprojectGeoJSON } from './kaart-import/gis.js';
import { parseDxf, flattenDxf, boreholeCandidates, guessDxfScale } from './kaart-import/dxf.js';
import { parseCsv, decodeText, readXlsx, guessColumns, rowsToRawPoints } from './kaart-import/table.js';

export const IMPORT_ACCEPT = '.geojson,.json,.kml,.kmz,.zip,.dxf,.csv,.txt,.xlsx,.pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff';

/**
 * Een PDF of afbeelding heeft geen coördinaten: die moet eerst uitgelijnd worden met paspunten
 * (de dialoog van de kaartimport) en wordt dan een achtergrondtekening, waarop je de locatie tekent.
 */
export const isDrawingFile = (name) => /\.(pdf|png|jpe?g|webp|tiff?)$/i.test(String(name));

const STRATEGY_CODES = new Set(NEN5740_STRATEGIES.map((s) => s.code));

/** Strategiecode uit een eigenschap of een label als 'Deellocatie A ONV-NL'. */
function splitStrategy(label) {
  const delen = String(label ?? '').trim().split(/\s+/);
  const laatste = delen.at(-1)?.toUpperCase();
  if (delen.length > 1 && STRATEGY_CODES.has(laatste)) return { name: delen.slice(0, -1).join(' '), strategy: laatste };
  return { name: delen.join(' '), strategy: '' };
}

const prop = (props, re) => {
  const k = Object.keys(props || {}).find((x) => re.test(x) && props[x] !== '' && props[x] != null);
  return k ? props[k] : null;
};

/** Diepte uit eigenschappen: depthCm (onze export), of een veld/omschrijving met 'm -mv'. */
function depthFrom(props) {
  const cm = Number(prop(props, /^depth_?cm$/i));
  if (Number.isFinite(cm) && cm > 0) return Math.round(cm);
  const veld = prop(props, /^(diepte|depth|einddiepte|boordiepte)/i);
  if (veld != null) return parseDepthCm(veld);
  const m = /([\d]+(?:[.,]\d+)?)\s*m\s*-?\s*mv/i.exec(String(props?.description ?? ''));
  return m ? parseDepthCm(`${m[1]} m`) : null;
}

/** Al geboord volgens het bestand? Die staan al als boring in het project. */
const isBored = (props) => /geboord/i.test(String(prop(props, /^status$/i) ?? props?.description ?? ''));

/** Ring zonder het herhaalde slotpunt. */
const open = (ring) => {
  const [a, b] = [ring[0], ring[ring.length - 1]];
  return ring.length > 3 && a[0] === b[0] && a[1] === b[1] ? ring.slice(0, -1) : ring;
};

/** FeatureCollection in WGS84 → { vlakken, punten }. */
export function splitFeatures(fc) {
  const vlakken = [];
  const punten = [];
  const naamVan = (p) => prop(p, /^(naam|name|nr|nummer|id|code|label|title)$/i);
  const vlak = (ring, props, i) => {
    const r = open(ring.map((c) => [c[0], c[1]]));
    if (r.length < 3) return;
    const label = splitStrategy(naamVan(props) ?? `Deellocatie ${i + 1}`);
    const strategy = String(prop(props, /^(strategy|strategie|strategy_?code)$/i) ?? label.strategy).toUpperCase();
    vlakken.push({ name: String(label.name), ring: r, strategy: STRATEGY_CODES.has(strategy) ? strategy : '' });
  };
  fc.features.forEach((f, i) => {
    const g = f.geometry;
    const props = f.properties || {};
    if (g.type === 'Polygon') vlak(g.coordinates[0], props, i);
    else if (g.type === 'MultiPolygon') g.coordinates.forEach((poly) => vlak(poly[0], props, i));
    else if (g.type === 'LineString' && g.coordinates.length >= 4) {
      const [a, b] = [g.coordinates[0], g.coordinates.at(-1)];
      if (a[0] === b[0] && a[1] === b[1]) vlak(g.coordinates, props, i);
    } else if (g.type === 'Point' || g.type === 'MultiPoint') {
      if (isBored(props) || props.type === 'deellocatie') return;
      const name = naamVan(props);
      const coords = g.type === 'Point' ? [g.coordinates] : g.coordinates;
      for (const c of coords) {
        punten.push({
          name: name != null ? String(name).trim() : `P${punten.length + 1}`,
          kind: normalizeKind(prop(props, /^(type|kind|soort)$/i), name),
          lng: c[0], lat: c[1], depthCm: depthFrom(props),
        });
      }
    }
  });
  return { vlakken, punten };
}

/** Punt in polygoon (ray casting), in de coördinaten van de tekening. */
function inPolygon([x, y], ring) {
  let binnen = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) binnen = !binnen;
  }
  return binnen;
}

/**
 * DXF: gesloten polylijnen worden vlakken (de naam is de tekst die erin staat), kleine
 * cirkels, punten en blokken met een naam worden boorpunten. Lagen met 'GEBOORD' slaan we over;
 * lagen met 'PEILBUIS' geven peilbuizen.
 */
export function splitDxf(text) {
  const dxf = parseDxf(text);
  const flat = flattenDxf(dxf);
  const monster = [...flat.points.map((p) => [p.x, p.y]), ...flat.lines.flatMap((l) => l.coords)].slice(0, 500);
  const schaal = guessDxfScale(dxf, monster, guessCoordinateSystem);
  const gok = guessCoordinateSystem(monster.map(([x, y]) => [x * schaal, y * schaal]));
  if (!gok) throw new Error('De tekening ligt niet in RD of een ander bekend stelsel.');
  const naarWgs = ([x, y]) => {
    const w = pointToWgs84(x * schaal, y * schaal, gok.system, gok.swapped);
    return w ? [w.x, w.y] : null;
  };

  const vlakken = [];
  flat.lines.filter((l) => l.closed && !l.circle && l.coords.length >= 4).forEach((l, i) => {
    const ring = open(l.coords);
    const tekst = flat.texts.find((t) => !t.attrib && inPolygon([t.x, t.y], ring));
    const label = splitStrategy(tekst?.text ?? `Deellocatie ${i + 1}`);
    const wgs = ring.map(naarWgs);
    if (wgs.every(Boolean)) vlakken.push({ name: label.name, ring: wgs, strategy: label.strategy });
  });

  const punten = [];
  for (const groep of boreholeCandidates(flat, { maxTextDistance: 5 / schaal })) {
    for (const p of groep.points) {
      if (!p.name || /geboord/i.test(p.layer)) continue;
      const c = naarWgs([p.x, p.y]);
      if (!c) continue;
      const kind = /peilbuis|^pb/i.test(p.layer) ? 'peilbuis' : normalizeKind(null, p.name);
      punten.push({ name: p.name, kind, lng: c[0], lat: c[1], depthCm: null });
    }
  }
  return { vlakken, punten };
}

/** CSV/Excel-meetstaat: alleen punten. Een kolom 'Status' met 'geboord' slaan we over. */
export function splitRows(rows) {
  const { headerRow, mapping } = guessColumns(rows);
  if (mapping.x < 0 || mapping.y < 0) throw new Error('Geen kolommen met X en Y gevonden.');
  const statusKolom = (rows[headerRow] || []).findIndex((c) => /^status$/i.test(String(c ?? '').trim()));
  const ruw = rowsToRawPoints(rows, headerRow, mapping)
    .filter((p) => statusKolom < 0 || !/geboord/i.test(String(rows[p.row - 1]?.[statusKolom] ?? '')));
  const gok = guessCoordinateSystem(ruw.map((p) => [p.x, p.y]));
  if (!gok) throw new Error('De coördinaten liggen niet in RD of een ander bekend stelsel.');
  const punten = [];
  for (const p of ruw) {
    const w = pointToWgs84(p.x, p.y, gok.system, gok.swapped);
    if (w) punten.push({ name: p.name, kind: p.kind, lng: w.x, lat: w.y, depthCm: p.plannedDepthCm });
  }
  return { vlakken: [], punten };
}

/**
 * Lees een bestand. `bytes` is een Uint8Array; de bestandsnaam bepaalt het formaat.
 * @returns {{ vlakken: {name, ring, strategy}[], punten: {name, kind, lng, lat, depthCm}[] }}
 */
export function readMonsterplanFile(name, bytes) {
  const ext = String(name).toLowerCase().split('.').pop();
  const tekst = () => decodeText(bytes);
  const gis = ({ fc, system }) => {
    const gok = system ? { system, swapped: false } : guessSystemForFc(fc);
    if (!gok) throw new Error('De coördinaten liggen niet in RD of een ander bekend stelsel.');
    return splitFeatures(gok.system === 'WGS84' && !gok.swapped ? fc : reprojectGeoJSON(fc, gok.system, gok.swapped));
  };
  if (ext === 'geojson' || ext === 'json') return gis(parseGeoJSON(tekst()));
  if (ext === 'kml') return gis(parseKml(tekst()));
  if (ext === 'kmz') return gis(parseKmz(bytes));
  if (ext === 'zip') return gis(parseShapefileZip(bytes));
  if (ext === 'dxf') return splitDxf(tekst());
  if (ext === 'csv' || ext === 'txt') return splitRows(parseCsv(tekst()).rows);
  if (ext === 'xlsx') {
    // Het eerste werkblad met herkenbare X/Y-kolommen.
    const bladen = readXlsx(bytes);
    const blad = bladen.find((b) => { const { mapping } = guessColumns(b.rows); return mapping.x >= 0 && mapping.y >= 0; }) || bladen[0];
    return splitRows(blad?.rows || []);
  }
  throw new Error(`Bestandstype .${ext} kan ik niet lezen.`);
}
